import { useCallback, useEffect, useState, ReactElement } from 'react';
import { Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/Reducers/NotificationSnackbarSlice';
import {
  EtaStatusService,
  IEtaStatusItem,
  EtaStatusType,
} from '../../api/services/etaStatusService';
import { CommonDialog, CommonDialogHeader } from '../../components/Dialog';

const STATUS_CONFIG: Record<EtaStatusType, { color: string; icon: ReactElement }> = {
  GOOD: { color: '#2e7d32', icon: <CheckCircleIcon sx={{ fontSize: 52, color: '#fff' }} /> },
  WARNING: { color: '#f57c00', icon: <WarningAmberIcon sx={{ fontSize: 52, color: '#fff' }} /> },
  CRITICAL: { color: '#c62828', icon: <CancelIcon sx={{ fontSize: 52, color: '#fff' }} /> },
};

const formatEndTime = (endTime: string) =>
  new Date(endTime).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export const ETAStatusPage = () => {
  const dispatch = useDispatch();

  const [statuses, setStatuses] = useState<IEtaStatusItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await EtaStatusService.getAllStatuses();
      setStatuses(data);
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to load ETA statuses.',
          severity: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const handleEnable = async (status: IEtaStatusItem) => {
    if (status.enabled) return;
    setTogglingId(status.id);
    try {
      await EtaStatusService.enableStatus(status.id);
      await fetchStatuses();
      dispatch(
        showNotification({ message: `${status.status} status enabled.`, severity: 'success' }),
      );
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to enable status.',
          severity: 'error',
        }),
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleSendNotification = async () => {
    setSendingNotification(true);
    setConfirmOpen(false);
    try {
      await EtaStatusService.sendOrdersDelayed();
      dispatch(
        showNotification({ message: 'Delayed orders notification sent.', severity: 'success' }),
      );
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message ?? 'Failed to send notification.',
          severity: 'error',
        }),
      );
    } finally {
      setSendingNotification(false);
    }
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          variant="contained"
          color="warning"
          startIcon={<SendIcon />}
          onClick={() => setConfirmOpen(true)}
          disabled={sendingNotification}
        >
          {sendingNotification ? 'Sending...' : 'Send Delayed Orders Notification'}
        </Button>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={fetchStatuses}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>

      {/* Loading state (initial load) */}
      {loading && statuses.length === 0 && (
        <Box display="flex" flexDirection="column" alignItems="center" gap={1} py={6}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading ETA status data...
          </Typography>
        </Box>
      )}

      {/* Status cards */}
      {statuses.length > 0 && (
        <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(260px, 1fr))" gap={3}>
          {statuses.map((status) => {
            const config = STATUS_CONFIG[status.status];
            const isToggling = togglingId === status.id;

            return (
              <Card
                key={status.id}
                sx={{
                  backgroundColor: config.color,
                  opacity: status.enabled ? 1 : 0.6,
                  transition: 'opacity 0.2s',
                  boxShadow: status.enabled ? 6 : 1,
                }}
              >
                <CardContent>
                  <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
                    {config.icon}

                    <Typography
                      variant="caption"
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.35)',
                        color: '#fff',
                        px: 2,
                        py: 0.5,
                        borderRadius: 10,
                        fontWeight: 700,
                        letterSpacing: 1,
                      }}
                    >
                      {status.status}
                    </Typography>

                    <Button
                      size="small"
                      variant="contained"
                      disabled={status.enabled || isToggling}
                      onClick={() => handleEnable(status)}
                      sx={{
                        minWidth: 110,
                        bgcolor: status.enabled ? 'success.dark' : 'rgba(0,0,0,0.3)',
                        '&:hover': {
                          bgcolor: status.enabled ? 'success.dark' : 'rgba(0,0,0,0.45)',
                        },
                        '&.Mui-disabled': {
                          bgcolor: status.enabled ? 'success.dark' : 'rgba(0,0,0,0.2)',
                          color: '#fff',
                        },
                      }}
                    >
                      {isToggling ? 'Updating...' : status.enabled ? 'Enabled' : 'Enable'}
                    </Button>

                    <Typography variant="body2" color="#fff" textAlign="center">
                      {status.message}
                    </Typography>

                    {status.endTime && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }} />
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                          Ends {formatEndTime(status.endTime)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Empty state */}
      {!loading && statuses.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={1}
          py={8}
          color="text.secondary"
        >
          <Typography variant="h6">No ETA status data found</Typography>
          <Typography variant="body2">Try refreshing to load the latest data.</Typography>
        </Box>
      )}

      {/* Send notification confirmation */}
      <CommonDialog
        isOpen={confirmOpen}
        header={
          <CommonDialogHeader
            title="Send Delayed Orders Notification"
            onClose={() => setConfirmOpen(false)}
          />
        }
        body={
          <Box mt={1}>
            <Typography variant="body2">
              This will send a push notification to all customers with delayed orders. Are you sure?
            </Typography>
          </Box>
        }
        footer={
          <Box display="flex" gap={1} justifyContent="flex-end" mt={2}>
            <Button variant="outlined" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" color="warning" onClick={handleSendNotification}>
              Send
            </Button>
          </Box>
        }
      />
    </Box>
  );
};
